import type { GridApi } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import type { ActualCostCompletenessStatus } from '@/common/api/types/itemFinancials';
import type { DatasetPage } from '@/common/hooks/useDatasetPages';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { actualCostRefreshSignal } from '@/common/sse/actualCostRefreshSignal';
import { ItemFinancialsAggregateApi } from '@/features/financials/api/item-financials-aggregate';
import { type PageData, pageQueryKey, useTablePagedQuery } from '../hooks/useTablePagedQuery';

export type ActualCostEntry = {
  value: string | undefined;
  status: ActualCostCompletenessStatus | undefined;
};

const EMPTY_ACTUAL_COSTS: ReadonlyMap<number, ActualCostEntry> = new Map();

type VisibleActualCostsContextType = {
  actualCostByItemId: ReadonlyMap<number, ActualCostEntry>;
};

const VisibleActualCostsContext = React.createContext<VisibleActualCostsContextType | undefined>(
  undefined,
);

export const useVisibleActualCost = (itemId: number | undefined): ActualCostEntry | undefined => {
  const context = React.useContext(VisibleActualCostsContext);
  if (!context || itemId === undefined) return undefined;
  return context.actualCostByItemId.get(itemId);
};

const fetchByIds = async (
  ids: readonly number[],
  signal: AbortSignal,
): Promise<ReadonlyMap<number, ActualCostEntry>> => {
  const financials = await ItemFinancialsAggregateApi.getItemFinancialsAggregate([...ids], signal);
  const actualCostById = new Map<number, ActualCostEntry>();
  for (const item of financials) {
    if (item.rolledActualCost != null || item.rolledActualCostStatus != null) {
      actualCostById.set(item.itemId, {
        value: item.rolledActualCost ?? undefined,
        status: item.rolledActualCostStatus ?? undefined,
      });
    }
  }
  return actualCostById;
};

export const VisibleActualCostsProvider = ({
  pages,
  visiblePageIndices,
  visibleItemIds,
  pageIndexById,
  datasetSignature,
  apiRef,
  instanceKey,
  enabled = true,
  children,
}: {
  pages: readonly DatasetPage[];
  visiblePageIndices: readonly number[];
  visibleItemIds: readonly number[];
  pageIndexById: ReadonlyMap<number, number>;
  datasetSignature: string;
  apiRef: React.RefObject<GridApi>;
  instanceKey: string;
  enabled?: boolean;
  children: React.ReactNode;
}) => {
  const { loadedById } = useTablePagedQuery<ActualCostEntry>({
    providerName: commonQueryKeys.itemsTable.visibleActualCosts,
    instanceKey,
    pages,
    visiblePageIndices,
    pageIndexById,
    datasetSignature,
    fetchByIds,
    enabled,
    apiRef,
  });

  const queryClient = useQueryClient();
  const refreshVersion = React.useSyncExternalStore(
    actualCostRefreshSignal.subscribe,
    actualCostRefreshSignal.getSnapshot,
    actualCostRefreshSignal.getSnapshot,
  );

  const stateRef = React.useRef({
    visibleItemIds,
    pageIndexById,
    instanceKey,
    datasetSignature,
    enabled,
  });
  stateRef.current = { visibleItemIds, pageIndexById, instanceKey, datasetSignature, enabled };

  const handledVersionRef = React.useRef(refreshVersion);

  React.useEffect(() => {
    if (refreshVersion === handledVersionRef.current) return;
    handledVersionRef.current = refreshVersion;
    const { visibleItemIds, pageIndexById, instanceKey, datasetSignature, enabled } =
      stateRef.current;
    if (!enabled || visibleItemIds.length === 0) return;

    const keyForPage = (pageIndex: number) =>
      pageQueryKey(
        commonQueryKeys.itemsTable.visibleActualCosts,
        instanceKey,
        datasetSignature,
        pageIndex,
      );

    // Only rows on cached pages; an unsubscribed page will fetch fresh when scrolled to.
    const idsByPage = new Map<number, number[]>();
    for (const id of visibleItemIds) {
      const pageIndex = pageIndexById.get(id);
      if (pageIndex === undefined) continue;
      if (!queryClient.getQueryData<PageData<ActualCostEntry>>(keyForPage(pageIndex))) continue;
      const bucket = idsByPage.get(pageIndex);
      if (bucket) bucket.push(id);
      else idsByPage.set(pageIndex, [id]);
    }
    if (idsByPage.size === 0) return;

    const idsToFetch = Array.from(idsByPage.values()).flat();

    const controller = new AbortController();
    void (async () => {
      const fresh = await fetchByIds(idsToFetch, controller.signal);
      if (controller.signal.aborted) return;
      for (const [pageIndex, ids] of idsByPage) {
        const key = keyForPage(pageIndex);
        const previous = queryClient.getQueryData<PageData<ActualCostEntry>>(key);
        if (!previous) continue;
        const valuesById = new Map(previous.valuesById);
        const missingIds = new Set(previous.missingIds);
        let changed = false;
        // The refresh queries every visible id, so an id absent from the response has no
        // cost anymore (e.g. team unassigned → rate gone). Clear it so the table doesn't
        // keep a stale value the detail panel has already dropped.
        for (const id of ids) {
          const value = fresh.get(id);
          if (value === undefined) {
            if (valuesById.delete(id)) {
              missingIds.add(id);
              changed = true;
            }
          } else {
            const current = valuesById.get(id);
            if (current?.value !== value.value || current?.status !== value.status) {
              valuesById.set(id, value);
              missingIds.delete(id);
              changed = true;
            }
          }
        }
        if (changed) {
          queryClient.setQueryData<PageData<ActualCostEntry>>(key, {
            ...previous,
            valuesById,
            missingIds,
          });
        }
      }
    })();

    return () => controller.abort();
  }, [refreshVersion, queryClient]);

  const value = React.useMemo<VisibleActualCostsContextType>(
    () => ({ actualCostByItemId: loadedById.size > 0 ? loadedById : EMPTY_ACTUAL_COSTS }),
    [loadedById],
  );

  return (
    <VisibleActualCostsContext.Provider value={value}>
      {children}
    </VisibleActualCostsContext.Provider>
  );
};
